import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Swords, ArrowLeft, Trophy, Target, ChevronRight, CheckCircle, XCircle, Minus, Loader2 } from 'lucide-react';
import { MobileBottomNav } from '@/components/MobileNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL?.replace(/^http:\/\//i, 'https://') + '/api';

const AIChallengesPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('create');
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [history, setHistory] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sport, setSport] = useState('Soccer');
  const token = localStorage.getItem('token');

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    loadHistory();
    loadLeaderboard();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/ai-challenges/history`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (e) { console.error(e); }
  };

  const loadLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/ai-challenges/leaderboard`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (e) { console.error(e); }
  };

  const createChallenge = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/ai-challenges/create`, {
        method: 'POST', headers,
        body: JSON.stringify({ sport }),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveChallenge(data);
        setPredictions({});
        setTab('play');
        toast.success('Challenge erstellt!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch (e) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  const submitPredictions = async () => {
    if (!activeChallenge) return;
    const preds = Object.entries(predictions).map(([match_id, user_prediction]) => ({
      match_id, user_prediction,
    }));
    if (preds.length < (activeChallenge.matches || []).length) {
      toast.error('Bitte alle Tipps abgeben!');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/ai-challenges/predict/${activeChallenge.challenge_id}`, {
        method: 'POST', headers,
        body: JSON.stringify(preds),
      });
      if (res.ok) {
        const result = await res.json();
        setActiveChallenge(result);
        setTab('result');
        loadHistory();
        toast.success('Challenge abgeschlossen!');
      }
    } catch (e) {
      toast.error('Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  const predLabel = { home: 'Heim', away: 'Auswaerts', draw: 'Unentsch.' };

  return (
    <div className="min-h-screen bg-[#121212] pb-24">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Swords className="w-6 h-6 text-[#7B1FA2]" />
          <h1 className="text-xl font-bold text-white">User vs KI</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-5 p-1 bg-[#1E1E1E] rounded-xl border border-white/5">
          {[
            { id: 'create', label: 'Neue Challenge' },
            { id: 'history', label: 'Verlauf' },
            { id: 'ranking', label: 'Rangliste' },
          ].map(t => (
            <button
              key={t.id}
              data-testid={`challenge-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                tab === t.id ? 'bg-[#7B1FA2]/20 text-[#7B1FA2]' : 'text-gray-500 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Create Tab */}
        {tab === 'create' && (
          <div data-testid="challenge-create" className="space-y-4">
            <div className="p-5 bg-gradient-to-br from-[#7B1FA2]/10 to-transparent rounded-2xl border border-[#7B1FA2]/20">
              <div className="flex items-center gap-3 mb-3">
                <Brain className="w-8 h-8 text-[#7B1FA2]" />
                <div>
                  <h2 className="text-lg font-bold text-white">Fordere die KI heraus!</h2>
                  <p className="text-xs text-gray-400">5 Matches — Wer tippt besser?</p>
                </div>
              </div>
              <div className="mb-4">
                <label className="text-xs text-gray-400 mb-1 block">Sportart</label>
                <div className="flex gap-2 flex-wrap">
                  {['Soccer', 'Basketball', 'Ice Hockey', 'all'].map(s => (
                    <button
                      key={s}
                      data-testid={`sport-${s}`}
                      onClick={() => setSport(s)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        sport === s
                          ? 'bg-[#7B1FA2]/20 border-[#7B1FA2]/40 text-[#7B1FA2]'
                          : 'bg-[#1E1E1E] border-white/5 text-gray-400 hover:text-white'
                      }`}
                    >
                      {s === 'all' ? 'Alle' : s === 'Ice Hockey' ? 'Eishockey' : s === 'Soccer' ? 'Fussball' : s}
                    </button>
                  ))}
                </div>
              </div>
              <button
                data-testid="create-challenge-btn"
                onClick={createChallenge}
                disabled={loading}
                className="w-full py-3 bg-[#7B1FA2] text-white rounded-xl font-bold text-sm hover:bg-[#7B1FA2]/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Swords className="w-4 h-4" />}
                Challenge starten
              </button>
            </div>
            {history?.stats && (
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 bg-[#1E1E1E] rounded-xl text-center border border-white/5">
                  <div className="text-lg font-bold font-mono text-[#00BFA6]">{history.stats.user_wins}</div>
                  <div className="text-[10px] text-gray-500">Du gewonnen</div>
                </div>
                <div className="p-3 bg-[#1E1E1E] rounded-xl text-center border border-white/5">
                  <div className="text-lg font-bold font-mono text-red-400">{history.stats.ai_wins}</div>
                  <div className="text-[10px] text-gray-500">KI gewonnen</div>
                </div>
                <div className="p-3 bg-[#1E1E1E] rounded-xl text-center border border-white/5">
                  <div className="text-lg font-bold font-mono text-gray-400">{history.stats.draws}</div>
                  <div className="text-[10px] text-gray-500">Unentschieden</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Play Tab */}
        {tab === 'play' && activeChallenge && (
          <div data-testid="challenge-play" className="space-y-3">
            <div className="text-sm text-gray-400 mb-2">Gib deine Tipps ab:</div>
            {(activeChallenge.matches || []).map((m, i) => (
              <div key={i} data-testid={`challenge-match-${i}`} className="p-4 bg-[#1E1E1E] rounded-xl border border-white/5">
                <div className="text-[10px] text-gray-500 mb-1">{m.league} | {m.sport}</div>
                <div className="text-sm font-medium text-white mb-3">{m.home_team} vs {m.away_team}</div>
                <div className="flex gap-2">
                  {['home', 'draw', 'away'].map(pred => (
                    <button
                      key={pred}
                      onClick={() => setPredictions(p => ({ ...p, [m.match_id]: pred }))}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                        predictions[m.match_id] === pred
                          ? 'bg-[#00BFA6]/20 border-[#00BFA6]/40 text-[#00BFA6]'
                          : 'bg-[#121212] border-white/5 text-gray-400 hover:text-white'
                      }`}
                    >
                      {pred === 'home' ? m.home_team?.split(' ').slice(-1)[0] : pred === 'away' ? m.away_team?.split(' ').slice(-1)[0] : 'X'}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button
              data-testid="submit-predictions-btn"
              onClick={submitPredictions}
              disabled={submitting}
              className="w-full py-3 bg-[#00BFA6] text-black rounded-xl font-bold text-sm hover:bg-[#00BFA6]/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
              Tipps abgeben
            </button>
          </div>
        )}

        {/* Result Tab */}
        {tab === 'result' && activeChallenge && (
          <div data-testid="challenge-result" className="space-y-4">
            <div className={`p-5 rounded-2xl border text-center ${
              activeChallenge.winner === 'user' ? 'bg-[#00BFA6]/10 border-[#00BFA6]/30' :
              activeChallenge.winner === 'ai' ? 'bg-red-500/10 border-red-500/30' :
              'bg-gray-500/10 border-gray-500/30'
            }`}>
              <div className="text-3xl font-black font-mono text-white mb-1">
                {activeChallenge.user_correct} : {activeChallenge.ai_correct}
              </div>
              <div className="text-sm font-bold mb-1" style={{
                color: activeChallenge.winner === 'user' ? '#00BFA6' : activeChallenge.winner === 'ai' ? '#ef4444' : '#9ca3af'
              }}>
                {activeChallenge.winner === 'user' ? 'Du hast gewonnen!' :
                 activeChallenge.winner === 'ai' ? 'KI hat gewonnen!' : 'Unentschieden!'}
              </div>
              <div className="text-xs text-gray-500">Du vs KI</div>
            </div>
            {(activeChallenge.matches || []).map((m, i) => (
              <div key={i} className="p-3 bg-[#1E1E1E] rounded-xl border border-white/5">
                <div className="text-[10px] text-gray-500 mb-1">{m.league}</div>
                <div className="text-sm text-white mb-1">{m.home_team} {m.home_score}:{m.away_score} {m.away_team}</div>
                <div className="flex gap-3 text-xs">
                  <span className="flex items-center gap-1">
                    {m.user_prediction === m.actual_result
                      ? <CheckCircle className="w-3 h-3 text-[#00BFA6]" />
                      : <XCircle className="w-3 h-3 text-red-400" />}
                    Du: {predLabel[m.user_prediction] || '-'}
                  </span>
                  <span className="flex items-center gap-1">
                    {m.ai_prediction === m.actual_result
                      ? <CheckCircle className="w-3 h-3 text-[#00BFA6]" />
                      : <XCircle className="w-3 h-3 text-red-400" />}
                    KI: {predLabel[m.ai_prediction] || '-'}
                  </span>
                </div>
              </div>
            ))}
            <button
              onClick={() => { setTab('create'); setActiveChallenge(null); }}
              className="w-full py-3 bg-[#7B1FA2] text-white rounded-xl font-bold text-sm"
            >
              Nochmal spielen
            </button>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div data-testid="challenge-history" className="space-y-2">
            {(!history?.challenges || history.challenges.length === 0) ? (
              <div className="text-center py-12 text-gray-500 text-sm">Noch keine Challenges gespielt</div>
            ) : history.challenges.map((c, i) => (
              <div key={i} className="p-3 bg-[#1E1E1E] rounded-xl border border-white/5 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  c.winner === 'user' ? 'bg-[#00BFA6]/20 text-[#00BFA6]' :
                  c.winner === 'ai' ? 'bg-red-500/20 text-red-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {c.winner === 'user' ? <Trophy className="w-4 h-4" /> :
                   c.winner === 'ai' ? <Brain className="w-4 h-4" /> :
                   <Minus className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white font-medium">
                    {c.user_correct}:{c.ai_correct} — {c.winner === 'user' ? 'Gewonnen' : c.winner === 'ai' ? 'Verloren' : 'Unentschieden'}
                  </div>
                  <div className="text-[10px] text-gray-500">{c.sport} | {new Date(c.completed_at).toLocaleDateString('de-DE')}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ranking Tab */}
        {tab === 'ranking' && (
          <div data-testid="challenge-ranking" className="space-y-2">
            {leaderboard.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">Noch keine Rangliste</div>
            ) : leaderboard.map((l, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[#1E1E1E] rounded-xl border border-white/5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                  i === 1 ? 'bg-gray-400/20 text-gray-400' :
                  i === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-white/5 text-gray-500'
                }`}>
                  {l.rank}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{l.username}</div>
                  <div className="text-[10px] text-gray-500">{l.total_challenges} Challenges</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold font-mono text-[#00BFA6]">{l.win_rate}%</div>
                  <div className="text-[10px] text-gray-500">Siegquote</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default AIChallengesPage;
